/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { FlatKitManualRoot, ModuleVariants, MultiModuleManualModule, MultiModuleManualRoot } from '@mta/vela-api-types'

/**
 * Type guard to check if a manual is a {@link MultiModuleManualRoot}.
 *
 * @param manual - the manual variant to narrow down
 */
const isMultiModuleManual = (manual: ModuleVariants): manual is MultiModuleManualRoot => {
  return manual.kit.content.type === 'multi-module'
}

/**
 * Type guard to check if a manual is a {@link FlatKitManualRoot} and specifically a single-module kit.
 *
 * @param manual - the manual variant to narrow down
 */
const isSingleModuleKitManual = (manual: ModuleVariants): manual is FlatKitManualRoot => {
  return manual.kit.content.type === 'single-module'
}

/**
 * Type guard to check if a manual is a {@link FlatKitManualRoot} and specifically a simple kit.
 *
 * @param manual - the manual variant to narrow down
 */
const isSimpleKitManual = (manual: ModuleVariants): manual is FlatKitManualRoot => {
  return manual.kit.content.type === 'simple'
}

/**
 * Type guard to check if a manual is a {@link FlatKitManualRoot} (either simple or single-module).
 *
 * @param manual - the manual variant to narrow down
 */
const isFlatKitManual = (manual: ModuleVariants): manual is FlatKitManualRoot => {
  return isSingleModuleKitManual(manual) || isSimpleKitManual(manual)
}

/**
 * Find a module by slug in a {@link MultiModuleManualRoot}.
 *
 * @param manual - the multi-module manual to search within
 * @param slug - slug of the activity to find
 */
const findModuleBySlug = (manual: MultiModuleManualRoot, slug: string) => {
  return manual.modules.find((m) => m.module.slug === slug)
}

/**
 * Find an activity by slug in a {@link MultiModuleManualRoot}.
 *
 * @param module - the multi-module manual module to search within
 * @param slug - the slug of the activity page to find
 */
const findActivityBySlug = (module: MultiModuleManualModule, slug: string) => {
  return module.activities.find((a) => a.activity.slug === slug)
}

/**
 * Find an activity by slug in a simple kit manual
 *
 * @param manual - the flat kit manual to search within
 * @param slug - slug of the activity to find
 */
const findSimpleActivityBySlug = (manual: FlatKitManualRoot, slug: string) => {
  return manual.activities.find((a) => a.slug === slug)
}

/**
 * Find a miscellaneous page by slug within a specific multi-module manual module.
 *
 * @param module - the multi-module manual module to search within
 * @param slug - the slug of the miscellaneous page to find
 */
const findModuleMiscPageBySlug = (module: MultiModuleManualModule, slug: string) => {
  return module.misc_pages?.find((p) => p.slug === slug)
}

/**
 * Find a miscellaneous page by slug within a flat kit manual (kit-level misc page).
 *
 * @param manual - the flat kit manual to search within
 * @param slug - the slug of the miscellaneous page to find
 */
const findFlatKitMiscPageBySlug = (manual: FlatKitManualRoot, slug: string) => {
  return manual.misc_pages?.find((p) => p.slug === slug)
}

export {
  findModuleBySlug,
  findActivityBySlug,
  findSimpleActivityBySlug,
  isMultiModuleManual,
  isFlatKitManual,
  findModuleMiscPageBySlug,
  findFlatKitMiscPageBySlug,
}
